import {
  TextField,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);
const minuteSteps = 30;

export default function WorkingDayForm({ workingDay }) {
  const { t } = useTranslation();
  const workingTime = workingDay?.workingTime ?? [];
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    register
  } = useFormContext();

  // Watch the workingDay checkbox to conditionally require start and end time
  const isWorkingDay = useWatch({
    control,
    name: `workingDays.${workingDay?.day}.workingDay`,
  });

  const firstStartTime = useWatch({
    control,
    name: `workingDays.${workingDay?.day}.startTime.0`,
  });

  const secondStartTime = useWatch({
    control,
    name: `workingDays.${workingDay?.day}.startTime.1`,
  });

  const firstEndTime = useWatch({
    control,
    name: `workingDays.${workingDay?.day}.endTime.0`,
  });

  function getMinTime(minTime) {
    return dayjs(minTime).toDate();
  }

  function endTimeReset(startTime, endTime) {
    const endTimeValue = getValues(endTime);
    if (endTimeValue && isDateBefore(startTime, endTime)) {
      setValue(endTime, null);
    }
  }

  function isDateBefore(newTime, previousTime) {
    const parseNewTime = dayjs(newTime);
    const parsePreviousTime = dayjs(previousTime);
    return parseNewTime.isBefore(parsePreviousTime);
  }

  React.useEffect(() => {
    if (workingDay?.workingTime[0]?.startTime) {
      setValue(`workingDays.${workingDay?.day}.startTime.0`, dayjs(workingDay?.workingTime[0]?.startTime, "HH:mm").toDate())
    }
    
    if (workingDay?.workingTime[0]?.endTime) {
      setValue(`workingDays.${workingDay?.day}.endTime.0`, dayjs(workingDay?.workingTime[0]?.endTime, "HH:mm").toDate())
    }

    if (workingDay?.workingTime[1]?.startTime) {
      setValue(`workingDays.${workingDay?.day}.startTime.1`, dayjs(workingDay?.workingTime[1]?.startTime, "HH:mm").toDate())
    }
  }, []);

  return (
    <React.Fragment>
      <Controller
        name={`workingDays.${workingDay?.day}.id`}
        control={control}
        defaultValue={workingDay?.id}
        render={({ field }) => <input type="hidden" {...field} />}
      />

      <Typography variant="caption" color={"GrayText"}>
        {t("FirstShift")}
      </Typography>
      <Grid container spacing={2} mb={3} mt={1}>
        <Grid item xs={12} md={4}>
          <Controller
            name={`workingDays.${workingDay?.day}.startTime.0`}
            control={control}
            defaultValue={
              workingTime[0]?.startTime
                ? dayjs(workingTime[0]?.startTime, "HH:mm").toDate()
                : null
            }
            rules={{
              required: isWorkingDay ? t("CantBeEmpty") : false,
            }}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl
                  fullWidth
                  error={
                    !!errors?.workingDays?.[workingDay?.day]?.startTime?.[0]
                  }
                >
                  <MobileTimePicker
                    ampm={false}
                    minutesStep={minuteSteps}
                    label={t("StartTime")}
                    slotProps={{
                      actionBar: {
                        actions: ['clear','cancel','accept']
                      },
                      textField: {
                        inputProps: { "data-testid": `clinic.working-hours.day.${workingDay?.day}.shift-1.start-input` }
                      }
                    }}
                    value={field?.value ?? null}
                    onChange={(newValue) => {
                      endTimeReset(newValue, `workingDays.${workingDay?.day}.endTime.0`)
                      field.onChange(newValue)
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                  <FormHelperText>
                    {
                      errors?.workingDays?.[workingDay?.day]?.startTime?.[0]
                        ?.message
                    }
                  </FormHelperText>
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name={`workingDays.${workingDay?.day}.endTime.0`}
            control={control}
            defaultValue={
              workingTime[0]?.endTime
                ? dayjs(workingTime[0]?.endTime, "HH:mm").toDate()
                : null
            }
            rules={{
              required: isWorkingDay ? t("CantBeEmpty") : false,
              validate: (value) => {
                if (isWorkingDay && !value) {
                  return t("CantBeEmpty");
                }
                return true;
              },
            }}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl
                  fullWidth
                  error={!!errors?.workingDays?.[workingDay?.day]?.endTime?.[0]}
                >
                  <MobileTimePicker
                    ampm={false}
                    minutesStep={minuteSteps}
                    label={t("EndTime")}
                    slotProps={{
                      actionBar: {
                        actions: ['clear','cancel','accept']
                      },
                      textField: {
                        inputProps: { "data-testid": `clinic.working-hours.day.${workingDay?.day}.shift-1.end-input` }
                      }
                    }}
                    value={field?.value ?? null}
                    onChange={(newValue) => field.onChange(newValue)}
                    minTime={getMinTime(
                      firstStartTime
                    )}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                  <FormHelperText>
                    {
                      errors?.workingDays?.[workingDay?.day]?.endTime?.[0]
                        ?.message
                    }
                  </FormHelperText>
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            name={`workingDays.${workingDay?.day}.appointmentsAtOnce.0`}
            control={control}
            defaultValue={workingDay?.workingTime[0]?.appointmentsAtOnce}
            label={t("AppointmentsAtOnce")}
            type="number"
            InputProps={{ inputProps: { min: 1, "data-testid": `clinic.working-hours.day.${workingDay?.day}.shift-1.at-once-input` }}}
            {...register(`workingDays.${workingDay?.day}.appointmentsAtOnce.0`)}
          />
        </Grid>
      </Grid>
      <Divider/>
      <Typography variant="caption" color={"GrayText"}>
        {t("SecondShift")}
      </Typography>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={4}>
          <Controller
            name={`workingDays.${workingDay?.day}.startTime.1`}
            control={control}
            defaultValue={
              workingTime[1]?.startTime
                ? dayjs(workingTime[1]?.startTime, "HH:mm").toDate()
                : null
            }
            rules={{
              validate: (value) => {
                if (
                  isWorkingDay &&
                  value &&
                  firstEndTime &&
                  isDateBefore(value, firstEndTime)
                ) {
                  return t("CantStartBeforeLastShift");
                }
                return true;
              },
            }}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl
                  fullWidth
                  error={
                    !!errors?.workingDays?.[workingDay?.day]?.startTime?.[1]
                  }
                >
                  <MobileTimePicker
                    ampm={false}
                    minutesStep={minuteSteps}
                    label={t("StartTime")}
                    slotProps={{
                      actionBar: {
                        actions: ['clear','cancel','accept']
                      },
                      textField: {
                        inputProps: { "data-testid": `clinic.working-hours.day.${workingDay?.day}.shift-2.start-input` }
                      }
                    }}
                    value={field?.value ?? null}
                    onChange={(newValue) => {
                      endTimeReset(newValue, `workingDays.${workingDay?.day}.endTime.1`)
                      field.onChange(newValue)
                    }}
                    minTime={getMinTime(
                      firstEndTime
                    )}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                  <FormHelperText>
                    {
                      errors?.workingDays?.[workingDay?.day]?.startTime?.[1]
                        ?.message
                    }
                  </FormHelperText>
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name={`workingDays.${workingDay?.day}.endTime.1`}
            control={control}
            defaultValue={
              workingTime[1]?.endTime
                ? dayjs(workingTime[1]?.endTime, "HH:mm").toDate()
                : null
            }
            rules={{
              validate: (value) => {
                if (isWorkingDay && !value && secondStartTime) {
                  return t("CantBeEmpty");
                }
                return true;
              },
            }}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl
                  fullWidth
                  error={!!errors?.workingDays?.[workingDay?.day]?.endTime?.[1]}
                >
                  <MobileTimePicker
                    ampm={false}
                    minutesStep={minuteSteps}
                    slotProps={{
                      actionBar: {
                        actions: ['clear','cancel','accept']
                      },
                      textField: {
                        inputProps: { "data-testid": `clinic.working-hours.day.${workingDay?.day}.shift-2.end-input` }
                      }
                    }}
                    label={t("EndTime")}
                    value={field?.value ?? null}
                    onChange={(newValue) => {
                      field.onChange(newValue)
                    }}
                    minTime={getMinTime(secondStartTime)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                  <FormHelperText>
                    {
                      errors?.workingDays?.[workingDay?.day]?.endTime?.[1]
                        ?.message
                    }
                  </FormHelperText>
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
              name={`workingDays.${workingDay?.day}.appointmentsAtOnce.1`}
              control={control}
              defaultValue={workingDay?.workingTime[1]?.appointmentsAtOnce ?? 1}
              label={t("AppointmentsAtOnce")}
              type="number"
              InputProps={{ inputProps: { min: 1, "data-testid": `clinic.working-hours.day.${workingDay?.day}.shift-2.at-once-input` }}}
              {...register(`workingDays.${workingDay?.day}.appointmentsAtOnce.1`)}
            />
          </Grid>
      </Grid>
    </React.Fragment>
  );
}
